import { mutate } from 'swr'

import { Item } from '@/interfaces/cash-register/Item'
import { Sku } from '@/interfaces/catalog/Sku'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import { Warehouse } from '@/interfaces/warehouse/Warehouse'

export function findDefaultWarehouse(warehouses: Warehouse[]): Warehouse | undefined {
	return warehouses.find((warehouse) => warehouse.isDefault)
}

export function buildStockBySkuId(stockItems: StockItem[]): Map<string, number> {
	const map = new Map<string, number>()

	for (const stockItem of stockItems) {
		const existing = map.get(stockItem.skuId) ?? 0
		map.set(stockItem.skuId, existing + stockItem.quantityAvailable)
	}

	return map
}

export function quantityInCart(items: Item[], skuId: string): number {
	return items
		.filter((item) => item.skuId === skuId)
		.reduce((sum, item) => sum + item.amount, 0)
}

export function isStockTracked(
	skuId: string,
	stockBySkuId: Map<string, number> | null,
): boolean {
	return stockBySkuId !== null && stockBySkuId.has(skuId)
}

export function availableToAdd(
	skuId: string,
	items: Item[],
	stockBySkuId: Map<string, number> | null,
): number {
	if (!isStockTracked(skuId, stockBySkuId)) {
		return Number.POSITIVE_INFINITY
	}

	const totalStock = stockBySkuId!.get(skuId) ?? 0

	return Math.max(0, totalStock - quantityInCart(items, skuId))
}

export function maxQuantityForLine(
	skuId: string,
	items: Item[],
	lineIndex: number,
	stockBySkuId: Map<string, number> | null,
): number {
	if (!isStockTracked(skuId, stockBySkuId)) {
		return Number.POSITIVE_INFINITY
	}

	const totalStock = stockBySkuId!.get(skuId) ?? 0
	const otherCartQuantity = items
		.filter((item, index) => index !== lineIndex && item.skuId === skuId)
		.reduce((sum, item) => sum + item.amount, 0)

	return Math.max(0, totalStock - otherCartQuantity)
}

export function isSkuOutOfStock(
	skuId: string,
	items: Item[],
	stockBySkuId: Map<string, number> | null,
): boolean {
	return availableToAdd(skuId, items, stockBySkuId) <= 0
}

export function isProductOutOfStock(
	productSkus: Sku[],
	items: Item[],
	stockBySkuId: Map<string, number> | null,
): boolean {
	if (productSkus.length === 0) {
		return false
	}

	return productSkus.every((sku) => isSkuOutOfStock(sku.id, items, stockBySkuId))
}

export function revalidateStockItems(): void {
	void mutate((key) => typeof key === 'string' && key.startsWith('/api/v2/stock-items'))
}
